import { ref, computed } from 'vue';
import { debounce } from '@/utils/api';

/**
 * Swiper 导航功能
 * 包含基础导航控制和菜单交互功能
 */
export const useSwiperNavigation = centralDispatcher => {
	const swiperInstance = ref();
	const activeMenuId = ref();

	const activeSwiperIndex = computed(() => {
		if (!swiperInstance.value) return 0;
		return swiperInstance.value.activeIndex;
	});

	const prevSwiper = () => {
		swiperInstance.value?.slidePrev();
	};

	const nextSwiper = () => {
		swiperInstance.value?.slideNext();
	};

	const updateActiveMenuId = id => {
		activeMenuId.value = id;
	};

	// 初始化活动菜单
	const initActiveMenu = () => {
		// 通过派发器获取数据，避免参数传递
		const { currentMenus } = centralDispatcher.getState();
		if (currentMenus.length) {
			updateActiveMenuId(currentMenus[0].id);
		}
	};

	// 菜单切换逻辑
	const switchMenu = debounce(
		function ({ menu, module }) {
			const menuItem = menu;
			const activeModuleId = module?.moduleId;

			// 通过派发器获取数据和回调，避免参数传递
			const { currentMenus } = centralDispatcher.getState();
			const { nodesMap } = centralDispatcher.getConfig();
			const stopScrollObserverCallback = centralDispatcher.stopScrollObserver;

			let moduleId = null;
			const { moduleVOS = [] } = menuItem || {};
			if (activeModuleId) {
				// 直接的目标模块
				moduleId = activeModuleId;
			} else {
				// 默认找一个模块
				moduleId = moduleVOS?.[0]?.moduleId;
			}
			if (!moduleId) {
				console.warn('switchMenu: moduleId is required');
				return;
			}

			const navWrapperNode = document.querySelector(nodesMap.swiperContainer);
			if (!navWrapperNode) {
				console.warn('switchMenu: navWrapperNode not found');
				return;
			}

			// 根据模块ID找到对应的菜单
			let findMenuIdx = -1;
			let targetMenuId = null;
			let moduleIndexInMenu = -1;

			for (let i = 0; i < currentMenus.length; i++) {
				const menu = currentMenus[i];
				if (menu.moduleVOS) {
					const moduleIndex = menu.moduleVOS.findIndex(module => module.moduleId === moduleId);
					if (moduleIndex !== -1) {
						findMenuIdx = i;
						targetMenuId = menu.id;
						moduleIndexInMenu = moduleIndex;
						break;
					}
				}
			}
			if (findMenuIdx === -1 || !targetMenuId) {
				console.warn(`switchMenu: Module with id ${moduleId} not found in menus`);
				return;
			}

			// 获取菜单DOM节点
			const menuNodeList = document.querySelectorAll(nodesMap.menuItem);
			const findMenuNode = menuNodeList[findMenuIdx];
			if (!findMenuNode) {
				console.warn(`switchMenu: Menu DOM node not found at index ${findMenuIdx}`);
				return;
			}

			// 获取模块DOM节点
			const moduleNodeList = document.querySelectorAll(nodesMap.moduleBlock);
			let targetModuleNode = null;

			// 计算模块在整个模块列表中的绝对索引
			let absoluteModuleIndex = 0;
			for (let i = 0; i < findMenuIdx; i++) {
				absoluteModuleIndex += currentMenus[i]?.moduleVOS?.length || 0;
			}
			absoluteModuleIndex += moduleIndexInMenu;

			targetModuleNode = moduleNodeList[absoluteModuleIndex];
			if (!targetModuleNode) {
				console.warn(`switchMenu: Module DOM node not found at index ${absoluteModuleIndex}`);
				return;
			}

			// 手动切换时暂停滚动观察器
			stopScrollObserverCallback();
			updateActiveMenuId(targetMenuId);

			// 计算滚动位置
			const styles = getComputedStyle(targetModuleNode);
			const modulePaddingTop = parseFloat(styles.paddingTop) || 0;
			const moduleTop = targetModuleNode.offsetTop;
			const targetScrollTop = moduleTop - navWrapperNode.offsetHeight + modulePaddingTop;

			// 执行平滑滚动
			window.scrollTo({
				top: targetScrollTop,
				behavior: 'smooth'
			});
		},
		300,
		true
	);

	// 更新活动菜单并自动切换 swiper
	const updateActiveMenuIdWithSwiper = menuId => {
		const currentActiveId = activeMenuId.value;
		if (currentActiveId === menuId) return;

		updateActiveMenuId(menuId);

		// 获取分组数据
		const swiperChunks = centralDispatcher.getComposeMenus();

		// 查找菜单在二维数组中的位置
		let foundSwiperIndex = -1;
		for (let i = 0; i < swiperChunks.length; i++) {
			for (let j = 0; j < swiperChunks[i].length; j++) {
				if (swiperChunks[i][j].id === menuId) {
					foundSwiperIndex = i;
					break;
				}
			}
			if (foundSwiperIndex !== -1) break;
		}

		// 比较 foundSwiperIndex 和 activeSwiperIndex 并调用切换函数
		if (foundSwiperIndex !== -1 && foundSwiperIndex !== activeSwiperIndex.value) {
			if (foundSwiperIndex < activeSwiperIndex.value) {
				prevSwiper();
			} else if (foundSwiperIndex > activeSwiperIndex.value) {
				nextSwiper();
			}
		}
	};

	return {
		swiperInstance,
		activeMenuId,
		activeSwiperIndex,
		prevSwiper,
		nextSwiper,
		updateActiveMenuId,
		initActiveMenu,
		switchMenu,
		updateActiveMenuIdWithSwiper
	};
};
